import { dist2, iatan2, idiv, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy } from '../types.ts';
import { ev } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The ink in the water (El pozo 1, docs/SAGAS.md): the tower defense the other way round. The nightmares climb out of
 * Musgoviejo's well in the middle of the square and go for the eight houses round its edge; a hero near one (or nearer
 * than its house) draws it off. Later the mill's stone stands ready at the edge: a hero by it pushes it towards the
 * well, slowly alone and quickly with another. On the well, it seals it: the ink left in the square drains away, won.
 * Too many houses burned loses it.
 */

export const wellTick = (s: BattleState): void => {
  const r = s.rules?.well;
  if (!r) return;
  const st = (s.well ??= { hp: r.houses.map(() => r.hp), stone: null, sealed: 0 });
  if (!s.started || st.sealed) return;
  // The mill's stone, ready at the edge of the square.
  if (!st.stone && s.waveT >= r.stoneAt) {
    st.stone = { x: r.mill.x, y: r.mill.y, at: 0 };
    ev(s, { t: 'well', kind: 'stone', i: -1, x: r.mill.x, y: r.mill.y });
  }
  const stone = st.stone;
  if (!stone) return;
  let by = 0;
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, stone.x, stone.y, r.reach)) by++;
  if (by === 0) return;
  // Along its way: down the road, then on to the well.
  const to = r.route[stone.at + 1] ?? { x: 0, y: 0 };
  const o = { x: stone.x, y: stone.y, facing: 0 };
  if (stepToward(o, to.x, to.y, by >= 2 ? r.fast : r.slow) && stone.at + 1 < r.route.length - 1) stone.at++;
  stone.x = o.x;
  stone.y = o.y;
  if (!withinRange(stone.x, stone.y, 0, 0, r.seal)) return;
  // On the well: sealed. What is left in the square drains away with the ink.
  st.sealed = s.tick;
  stone.x = 0;
  stone.y = 0;
  for (const e of s.enemies) if (e.hp > 0) e.hp = 0;
  ev(s, { t: 'well', kind: 'seal', i: -1, x: 0, y: 0 });
};

/** A nightmare out of the well: at the nearest house still standing, unless a hero is near it or nearer than that house. */
export const wellChase = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.well;
  const st = s.well;
  if (!r || !st || e.taunter !== 0) return false;
  let at = -1;
  let bd = 0x7fffffff;
  r.houses.forEach((h, i) => {
    if ((st.hp[i] ?? 0) <= 0) return;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      at = i;
    }
  });
  const house = r.houses[at];
  if (!house) return false;
  for (const h of s.heroes) if (h.downTicks === 0 && (withinRange(e.x, e.y, h.x, h.y, r.guard) || dist2(e.x, e.y, h.x, h.y) < bd)) return false;
  e.tgtKind = 0;
  e.tgtId = 0;
  if (!withinRange(e.x, e.y, house.x, house.y, r.door)) {
    e.anim = 'walk';
    stepToward(e, house.x, house.y, px);
    return true;
  }
  e.facing = iatan2(house.y - e.y, house.x - e.x);
  if (e.cd > 0) return true;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  st.hp[at] = (st.hp[at] ?? 0) - e.damage;
  ev(s, { t: 'well', kind: 'hit', i: at, x: house.x, y: house.y });
  if ((st.hp[at] ?? 0) <= 0) {
    st.hp[at] = 0;
    ev(s, { t: 'well', kind: 'burn', i: at, x: house.x, y: house.y });
  }
  return true;
};

/** Houses burned so far. */
export const wellBurned = (s: BattleState): number => (s.well?.hp ?? []).filter((x) => x <= 0).length;

/** Sealed: won (a moment later); `lose` houses burned: lost. */
export const wellResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.well;
  if (!r) return null;
  if (wellBurned(s) >= r.lose) return 'lost';
  return s.well?.sealed && s.tick - s.well.sealed >= r.beat ? 'won' : null;
};

/** How far along the stone is (0 to 100), for the HUD. */
export const wellStoneDone = (s: BattleState): number => {
  const r = s.rules?.well;
  const st = s.well?.stone;
  if (!r || !st) return 0;
  const all = isqrt(dist2(r.mill.x, r.mill.y, 0, 0));
  const left = isqrt(dist2(st.x, st.y, 0, 0));
  return all > 0 ? 100 - idiv(100 * left, all) : 100;
};
